<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  Badge,
  Button,
  EmptyState,
  Input,
  PageHeader,
  Section,
  SegmentedControl,
  Select,
  Stat,
  Table,
  Tabs,
  Toolbar,
} from '@drama/ui'
import { useI18n } from 'vue-i18n'

import {
  fetchUsageCostByBillingMode,
  fetchUsageCostByModel,
  fetchUsageCostByTime,
  fetchUsageCostByUser,
  fetchUsageCostOverview,
  fetchUsageFacets,
} from '../../api/usage.js'
import { callsQueryOf, isoRangeOf, rangeOfTimeBucket, recentDaysOf, utcDayOf } from './cost-range.js'
import { failureToneOf, modelLabelOf } from './cost-metrics.js'
import { BILLED_CAPABILITIES } from './model-capability.js'
import CostDimensionChart from './cost/CostDimensionChart.vue'
import CostModelChart from './cost/CostModelChart.vue'
import CostTimeChart from './cost/CostTimeChart.vue'
import CostUserChart from './cost/CostUserChart.vue'
import { formatInt, formatMoney, formatRate } from './llm-format.js'

const USER_LIMIT = 20
const PRESET_DAYS = [1, 7, 30]
const METRICS = ['supplier_cost_micros', 'calls', 'charged_points']
// 成本看板只认计费能力：向量调用没有 billing_mode / charged_points，
// 它的供应商成本在「调用记录 · 向量」页签里逐条看。
const CAPABILITIES = BILLED_CAPABILITIES
const BILLING_MODES = ['record_only', 'fixed_points', 'cost_ratio', 'legacy_record_only', 'legacy_points']
const COST_STATES = ['all', 'priced', 'unpriced', 'charged']

const { t } = useI18n()
const router = useRouter()
const today = utcDayOf(new Date())
const days = ref(recentDaysOf(today, 7))
const bucket = ref('day')
const metric = ref('supplier_cost_micros')
const filters = ref({
  capability: 'text',
  provider: '',
  model: '',
  userId: '',
  billingMode: '',
  costState: 'all',
})
const models = ref([])
const loading = ref(false)
const failure = ref('')
const rangeError = ref('')
const overview = ref(null)
const timeSlices = ref([])
const modelSlices = ref([])
const userSlices = ref([])
const billingSlices = ref([])
const table = ref('model')
const analysisTab = ref('billing')

const range = computed(() => {
  try {
    return isoRangeOf(days.value)
  } catch {
    return null
  }
})
const hasData = computed(() => overview.value !== null && overview.value.calls > 0)
const preset = computed({
  get() {
    for (const count of PRESET_DAYS) {
      const candidate = recentDaysOf(today, count)
      if (candidate.sinceDay === days.value.sinceDay && candidate.untilDay === days.value.untilDay) return count
    }
    return 0
  },
  set(count) {
    days.value = recentDaysOf(today, count)
  },
})
const tabs = computed(() => [
  { key: 'model', label: t('visualGameAdmin.llm.cost.tabs.model') },
  { key: 'user', label: t('visualGameAdmin.llm.cost.tabs.user') },
])
const analysisTabs = computed(() => [
  { key: 'billing', label: t('visualGameAdmin.llm.cost.analysis.billing') },
  { key: 'model', label: t('visualGameAdmin.llm.cost.analysis.model') },
  { key: 'user', label: t('visualGameAdmin.llm.cost.analysis.user') },
])
const bucketOptions = computed(() => ['day', 'hour'].map(value => ({
  value,
  label: t(`visualGameAdmin.llm.cost.bucket.${value}`),
})))
const metricOptions = computed(() => METRICS.map(value => ({
  value,
  label: t(`visualGameAdmin.llm.cost.metric.${value}`),
})))
const presetOptions = computed(() => PRESET_DAYS.map(value => ({
  value,
  label: t('visualGameAdmin.llm.cost.filter.recent', { days: value }),
})))
const capabilityTabs = computed(() => CAPABILITIES.map(value => ({
  key: value,
  label: t(`visualGameAdmin.llm.calls.capability.${value}`),
})))
const modelOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.llm.calls.filters.all') },
  ...models.value
    .filter(item => item.capability === filters.value.capability)
    .map(item => ({
      value: JSON.stringify([item.provider, item.model]),
      label: `${item.provider_display_name} · ${item.provider_capability_code} · ${item.model}`,
    })),
])
const selectedModel = computed({
  get() {
    if (filters.value.model === '') return ''
    return JSON.stringify([filters.value.provider, filters.value.model])
  },
  set(value) {
    if (value === '') {
      filters.value.provider = ''
      filters.value.model = ''
      return
    }
    const [provider, model] = JSON.parse(value)
    filters.value.provider = provider
    filters.value.model = model
  },
})
const billingOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.llm.calls.filters.all') },
  ...BILLING_MODES.map(value => ({
    value,
    label: t(`visualGameAdmin.llm.calls.billingMode.${value}`),
  })),
])
const costStateOptions = computed(() => COST_STATES.map(value => ({
  value,
  label: t(`visualGameAdmin.llm.calls.costState.${value}`),
})))
const cards = computed(() => {
  if (overview.value === null) return []
  return [
    { key: 'calls', value: formatInt(overview.value.calls), tone: 'neutral' },
    { key: 'cost', value: formatMoney(overview.value.supplier_cost_micros), tone: 'neutral' },
    { key: 'chargedPoints', value: formatInt(overview.value.charged_points), tone: 'neutral' },
    { key: 'recordOnlyCalls', value: formatInt(overview.value.record_only_calls), tone: 'neutral' },
    {
      key: 'failureRate',
      value: formatRate(overview.value.failure_rate),
      tone: failureToneOf(overview.value.calls, overview.value.failed_calls),
    },
  ]
})
const modelRows = computed(() => modelSlices.value.map(item => ({
  ...item,
  id: JSON.stringify([item.capability, item.provider, item.model]),
  name: modelLabelOf(item),
})))
const userRows = computed(() => userSlices.value.map(item => ({
  ...item,
  id: item.user_id ?? '__none__',
  name: item.user_id ?? t('visualGameAdmin.llm.cost.user.anonymous'),
})))
const rows = computed(() => table.value === 'model' ? modelRows.value : userRows.value)

function tableColumns(kind) {
  const name = kind === 'model'
    ? { key: 'name', label: t('visualGameAdmin.llm.cost.table.model'), width: '360px', mono: true }
    : { key: 'name', label: t('visualGameAdmin.llm.cost.table.user'), width: '220px', mono: true }
  return [
    name,
    { key: 'supplier_cost_micros', label: t('visualGameAdmin.llm.cost.table.cost'), width: '130px', align: 'right', mono: true },
    { key: 'calls', label: t('visualGameAdmin.llm.cost.table.calls'), width: '90px', align: 'right', mono: true },
    { key: 'chargedPoints', label: t('visualGameAdmin.llm.cost.table.chargedPoints'), width: '100px', align: 'right', mono: true },
    { key: 'recordOnly', label: t('visualGameAdmin.llm.cost.table.recordOnly'), width: '100px', align: 'right', mono: true },
    { key: 'failed', label: t('visualGameAdmin.llm.cost.table.failed'), width: '80px', align: 'right', mono: true },
    { key: 'failureRate', label: t('visualGameAdmin.llm.cost.table.failureRate'), width: '90px', align: 'right', mono: true },
    { key: 'unpriced', label: t('visualGameAdmin.llm.cost.table.unpriced'), width: '100px', align: 'right', mono: true },
    { key: 'drill', label: t('visualGameAdmin.llm.cost.table.drill'), width: '100px' },
  ]
}

function queryOfCurrentFilters() {
  const query = {
    ...isoRangeOf(days.value),
    capability: filters.value.capability,
    cost_state: filters.value.costState,
  }
  if (filters.value.model) {
    query.provider = filters.value.provider
    query.model = filters.value.model
  }
  if (filters.value.userId) query.user_id = filters.value.userId
  if (filters.value.billingMode) query.billing_mode = filters.value.billingMode
  return query
}

async function load() {
  rangeError.value = ''
  let query
  try {
    query = queryOfCurrentFilters()
  } catch (inputError) {
    rangeError.value = inputError.message
    return
  }
  const facetQuery = { ...query }
  delete facetQuery.provider
  delete facetQuery.model
  loading.value = true
  failure.value = ''
  try {
    const [nextOverview, byTime, byModel, byUser, byBilling, facets] = await Promise.all([
      fetchUsageCostOverview(query),
      fetchUsageCostByTime({ ...query, bucket: bucket.value }),
      fetchUsageCostByModel(query),
      fetchUsageCostByUser({ ...query, limit: USER_LIMIT }),
      fetchUsageCostByBillingMode(query),
      fetchUsageFacets(facetQuery),
    ])
    overview.value = nextOverview
    timeSlices.value = byTime.items
    modelSlices.value = byModel.items
    userSlices.value = byUser.items
    billingSlices.value = byBilling.items
    models.value = facets.models
  } catch (requestError) {
    failure.value = requestError.message
  } finally {
    loading.value = false
  }
}

function applyPreset(count) {
  days.value = recentDaysOf(today, count)
}

function onCapabilityChange(capability) {
  filters.value.capability = capability
  filters.value.provider = ''
  filters.value.model = ''
  return load()
}

function zoomToBucket(slice) {
  const zoomed = rangeOfTimeBucket(slice.time)
  days.value = { sinceDay: zoomed.since.slice(0, 10), untilDay: zoomed.since.slice(0, 10) }
  if (bucket.value === 'day') bucket.value = 'hour'
}

function callsTarget(overrides = {}) {
  const target = { range: range.value, capability: filters.value.capability }
  if (filters.value.model) {
    target.provider = filters.value.provider
    target.model = filters.value.model
  }
  if (filters.value.userId) target.userId = filters.value.userId
  if (filters.value.billingMode) target.billingMode = filters.value.billingMode
  if (filters.value.costState !== 'all') target.costState = filters.value.costState
  return { ...target, ...overrides }
}

function openCalls(overrides) {
  router.push({ path: '/llm/calls', query: callsQueryOf(callsTarget(overrides)) })
}

function drillToModelCalls(item) {
  openCalls({ capability: item.capability, provider: item.provider, model: item.model })
}

function drillToUserCalls(item) {
  openCalls({ userId: item.user_id })
}

function drillToBillingCalls(item) {
  openCalls({ billingMode: item.billing_mode })
}

function isDrillable(row) {
  return table.value === 'model' || row.user_id !== null
}

function drillToCalls(row) {
  if (table.value === 'model') drillToModelCalls(row)
  else drillToUserCalls(row)
}

watch([days, bucket], load, { deep: true, immediate: true })
</script>

<template>
  <div class="cost vga-page">
    <PageHeader :title="t('visualGameAdmin.llm.cost.title')">
      <template #actions>
        <Button size="sm" :disabled="!range" @click="openCalls({})">
          {{ t('visualGameAdmin.llm.cost.allCalls') }}
        </Button>
      </template>
    </PageHeader>

    <Section class="cost__tabs">
      <Tabs
        :model-value="filters.capability"
        :tabs="capabilityTabs"
        @update:model-value="onCapabilityChange"
      />
    </Section>

    <Section>
      <Toolbar>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.llm.cost.filter.since') }}</label>
          <Input v-model="days.sinceDay" type="date" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.llm.cost.filter.until') }}</label>
          <Input v-model="days.untilDay" type="date" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.llm.cost.filter.preset') }}</label>
          <SegmentedControl v-model="preset" :options="presetOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.llm.calls.filters.model') }}</label>
          <Select v-model="selectedModel" :options="modelOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.llm.calls.filters.billingMode') }}</label>
          <Select v-model="filters.billingMode" :options="billingOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.llm.calls.filters.costState') }}</label>
          <Select v-model="filters.costState" :options="costStateOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.llm.calls.filters.user') }}</label>
          <Input v-model="filters.userId" :placeholder="t('visualGameAdmin.llm.calls.filters.placeholder.user')" size="sm" />
        </div>

        <template #actions>
          <Button size="sm" :loading="loading" @click="load">
            {{ t('visualGameAdmin.llm.cost.filter.refresh') }}
          </Button>
        </template>
      </Toolbar>
      <p v-if="rangeError" class="vga-error cost__filter-error" role="alert">{{ rangeError }}</p>
    </Section>

    <Section v-if="failure">
      <div class="cost__failure">
        <p class="vga-error" role="alert">{{ failure }}</p>
        <Button variant="secondary" size="sm" @click="load">{{ t('visualGameAdmin.llm.cost.retry') }}</Button>
      </div>
    </Section>

    <template v-if="overview">
      <div class="cost__cards">
        <Stat
          v-for="card in cards"
          :key="card.key"
          :label="t(`visualGameAdmin.llm.cost.card.${card.key}`)"
          :value="card.value"
          :tone="card.tone"
        />
      </div>
      <p v-if="overview.unpriced_calls > 0" class="cost__unpriced" role="status">
        <Badge variant="warning">{{ t('visualGameAdmin.llm.cost.unpriced.badge') }}</Badge>
        <span>{{ t('visualGameAdmin.llm.cost.unpriced.short', { count: overview.unpriced_calls }) }}</span>
      </p>
    </template>

    <Section v-if="overview && !hasData">
      <EmptyState :title="t('visualGameAdmin.llm.cost.empty.title')">
        <template #action>
          <Button variant="secondary" size="sm" @click="applyPreset(30)">
            {{ t('visualGameAdmin.llm.cost.empty.action') }}
          </Button>
        </template>
      </EmptyState>
    </Section>

    <template v-if="hasData">
      <Section :title="t('visualGameAdmin.llm.cost.chart.timeTitle')">
        <Toolbar compact class="cost__chart-controls">
          <div class="drama-toolbar-field">
            <label>{{ t('visualGameAdmin.llm.cost.chart.bucketLabel') }}</label>
            <SegmentedControl v-model="bucket" :options="bucketOptions" size="sm" />
          </div>
          <div class="drama-toolbar-field">
            <label>{{ t('visualGameAdmin.llm.cost.chart.metricLabel') }}</label>
            <SegmentedControl v-model="metric" :options="metricOptions" size="sm" />
          </div>
        </Toolbar>
        <CostTimeChart :items="timeSlices" :metric="metric" @select="zoomToBucket" />
      </Section>

      <Section :title="t('visualGameAdmin.llm.cost.analysis.title')">
        <Tabs v-model="analysisTab" :tabs="analysisTabs">
          <template #billing>
            <CostDimensionChart dimension="billing" :items="billingSlices" @select="drillToBillingCalls" />
          </template>
          <template #model>
            <CostModelChart :items="modelSlices" @select="drillToModelCalls" />
          </template>
          <template #user>
            <CostUserChart :items="userSlices" @select="drillToUserCalls" />
          </template>
        </Tabs>
      </Section>

      <Section :title="t('visualGameAdmin.llm.cost.table.title')">
        <Tabs v-model="table" :tabs="tabs">
          <template #[table]>
            <Table :columns="tableColumns(table)" :rows="rows" row-key="id">
              <template #supplier_cost_micros="{ row }">{{ formatMoney(row.supplier_cost_micros) }}</template>
              <template #calls="{ row }">{{ formatInt(row.calls) }}</template>
              <template #chargedPoints="{ row }">{{ formatInt(row.charged_points) }}</template>
              <template #recordOnly="{ row }">{{ formatInt(row.record_only_calls) }}</template>
              <template #failed="{ row }">{{ formatInt(row.failed_calls) }}</template>
              <template #failureRate="{ row }">{{ formatRate(row.failure_rate) }}</template>
              <template #unpriced="{ row }">
                <Badge v-if="row.unpriced_calls > 0" variant="warning">{{ formatInt(row.unpriced_calls) }}</Badge>
                <span v-else>0</span>
              </template>
              <template #drill="{ row }">
                <Button v-if="isDrillable(row)" variant="ghost" size="sm" @click="drillToCalls(row)">
                  {{ t('visualGameAdmin.llm.cost.table.drillAction') }}
                </Button>
                <span v-else class="cost__muted">{{ t('visualGameAdmin.llm.cost.user.noDrilldown') }}</span>
              </template>
            </Table>
          </template>
        </Tabs>
      </Section>
    </template>
  </div>
</template>

<style scoped lang="scss">
.cost {
  &__tabs :deep(.drama-tabs__panel) {
    display: none;
  }

  &__filter-error {
    margin: var(--drama-space-md) 0 0;
    font-size: var(--drama-font-size-xs);
  }

  &__chart-controls {
    margin-bottom: var(--drama-space-lg);
  }

  &__failure {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--drama-space-lg);
  }

  &__cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: var(--drama-space-md);
  }

  &__unpriced {
    display: flex;
    align-items: center;
    gap: var(--drama-space-sm);
    margin: 0;
    color: var(--drama-color-text-muted);
    font-size: var(--drama-font-size-sm);
  }

  &__muted {
    color: var(--drama-color-text-muted);
    font-size: var(--drama-font-size-sm);
  }
}
</style>
